<template>
  <div class="dns-domain-input" data-testid="dns-domain-input">
    <a-form @submit.prevent="handleSubmit" data-testid="dns-lookup-form">
      <a-form-item
        :validate-status="validationStatus"
        :help="validationMessage"
        style="margin-bottom: 0;"
        data-testid="dns-domain-form-item"
      >
        <a-input-group compact size="large" class="input-group">
          <a-input
            v-model:value="localDomain"
            placeholder="Enter domain name (e.g., example.com)"
            size="large"
            :disabled="disabled"
            @blur="validateDomain"
            @keyup.enter="handleSubmit"
            class="domain-input"
            data-testid="dns-domain-input-field"
          >
            <template #prefix>
              <global-outlined />
            </template>
          </a-input>
          <a-button
            type="primary"
            html-type="submit"
            size="large"
            :loading="loading"
            :disabled="!isValid || disabled"
            class="lookup-button"
            data-testid="dns-lookup-button"
          >
            <template #icon>
              <search-outlined />
            </template>
            {{ buttonText }}
          </a-button>
        </a-input-group>
      </a-form-item>
    </a-form>
  </div>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { GlobalOutlined, SearchOutlined } from '@ant-design/icons-vue'

interface Props {
  modelValue: string
  loading?: boolean
  disabled?: boolean
  buttonText?: string
}

interface Emits {
  (e: 'update:modelValue', value: string): void
  (e: 'submit', domain: string): void
}

const props = withDefaults(defineProps<Props>(), {
  loading: false,
  disabled: false,
  buttonText: 'Look Up'
})

const emit = defineEmits<Emits>()

const localDomain = computed({
  get: () => props.modelValue,
  set: (value: string) => emit('update:modelValue', value)
})

const validationStatus = ref<'' | 'error' | 'success' | 'warning'>('')
const validationMessage = ref('')
const isValid = ref(false)

const validateDomain = () => {
  const domain = localDomain.value.trim()
  
  if (!domain) {
    validationStatus.value = ''
    validationMessage.value = ''
    isValid.value = false
    return
  }

  // Remove protocol if present
  const cleanDomain = domain.replace(/^https?:\/\//, '').replace(/\/$/, '')
  
  // Basic domain validation
  const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/
  
  if (!domainRegex.test(cleanDomain)) {
    validationStatus.value = 'error'
    validationMessage.value = 'Please enter a valid domain name'
    isValid.value = false
    return
  }

  // Check for IP address (not allowed)
  const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/
  if (ipRegex.test(cleanDomain)) {
    validationStatus.value = 'error'
    validationMessage.value = 'IP addresses are not supported. Please enter a domain name'
    isValid.value = false
    return
  }

  validationStatus.value = 'success'
  validationMessage.value = ''
  isValid.value = true
  
  // Update with cleaned domain
  if (cleanDomain !== domain) {
    emit('update:modelValue', cleanDomain)
  }
}

const handleSubmit = () => {
  validateDomain()
  if (isValid.value) {
    emit('submit', localDomain.value.trim())
  }
}
</script>

<style scoped lang="scss">
.dns-domain-input {
  max-width: 100%;
  
  .input-group {
    display: flex;
    width: 100%;
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: 12px;
    }
    
    .domain-input {
      flex: 1;
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
      
      @media (max-width: 768px) {
        border-radius: 8px !important;
      }
      
      :deep(.ant-input) {
        font-size: 16px;
        font-weight: 500;
        padding: 12px 16px;

        @media (max-width: 768px) {
          padding: 12px 32px !important;
        }
      }
      
      :deep(.ant-input-prefix) {
        margin-right: 12px;
        font-size: 18px;
      }
    }
    
    .lookup-button {
      border-top-left-radius: 0 !important;
      border-bottom-left-radius: 0 !important;
      font-weight: 600;
      font-size: 16px;
      height: 64px;
      line-height: initial;
      padding: 0 32px;
      white-space: nowrap;
      box-shadow: none;
      
      @media (max-width: 768px) {
        border-radius: 8px !important;
        width: 100%;
      }
      
      &:hover, &:focus {
        box-shadow: none;
      }
    }
  }
  
  :deep(.ant-form-item-explain-error) {
    margin-top: 8px;
    font-weight: 500;
  }
}
</style>
